import { fetchAsset } from "../core/AssetSource.js";
import { TimeControlsUI } from "./TimeControlsUI.js";
import { FloorCropModal } from "./FloorCropModal.js";
import { LayoutPickerModal } from "./LayoutPickerModal.js";
import { EmbedController } from "./EmbedController.js";
import { FurnitureGrid } from "./FurnitureGrid.js";
import { FabricGrid } from "./FabricGrid.js";
import { DevToolsPanel } from "./DevToolsPanel.js";
import { EnvironmentPanel } from "./EnvironmentPanel.js";
import { HelpTutorials } from "./HelpTutorials.js";
import { ShareModal } from "./ShareModal.js";
import { DEFAULT_WOOD_TYPE, DEFAULT_FABRIC_CODE, migrateShadowQuality, lookupProductRow, isFreePlaceable, isFreeStanding } from "../utils/constants.js";
import { showLoadingCursor, hideLoadingCursor, clearFurnitureSelection } from "../utils/ui.js";
import { activeProductCode } from "../utils/variants.js";
import { SHOW_DEV_TOOLS } from "../config.js";
import { readCustomFloor, saveCustomFloor, clearCustomFloor } from "../core/UserPreferences.js";
import {
  saveFile,
  shareOrCopy,
  copyText,
  hasShareSheet,
  saveFloorImage,
  arAvailable,
  presentAR,
} from "../platform/index.js";

// API short descriptions that name no kind of piece (every matacao3 module's is "Element"); matched on the English source so every language drops them.
const UNINFORMATIVE_DESCRIPTIONS = new Set(['element']);

// WebKit answers a WebP request with a PNG, so ask once rather than encode twice per save.
let webpEncodable;
function canEncodeWebp() {
  if (webpEncodable === undefined) {
    const probe = document.createElement('canvas');
    probe.width = probe.height = 1;
    webpEncodable = probe.toDataURL('image/webp').startsWith('data:image/webp');
  }
  return webpEncodable;
}

export class UIManager {
  constructor(app) {
    this.app = app;
    this.activeTab = "furniture"; // furniture | material — which grid the bar is showing
    this.uiStyle = "layout1"; // layout1 = classic bar, layout2 = docked drawer
    this.selectedFurnitureType = null;
    this.selectedMaterial = null; // Will be set to ET-14 after materials load
    this.lastSelectedColor = null; // Fabric code of the last pick; restored from UserPreferences
    this.currentCategory = null; // Will be set dynamically after data loads
    this.componentCategories = {}; // Component categorization system
    this.barCollapsed = false; // Whether the bottom bar is collapsed
    this._insetBottom = 0;       // Last canvas inset applied, so _syncCanvasInset early-outs

    // Time controls
    this.timeControlsUI = null;

    // Localization
    this.localization = null;
  }

  // Sub-views are built on first use: tests construct a bare UIManager via Object.create.
  get layoutPicker() { return (this._layoutPicker ??= new LayoutPickerModal(this.app, this)); }
  get embed() { return (this._embed ??= new EmbedController(this.app, this)); }
  get furnitureGrid() { return (this._furnitureGrid ??= new FurnitureGrid(this.app, this)); }
  get fabricGrid() { return (this._fabricGrid ??= new FabricGrid(this.app, this)); }
  get devTools() { return (this._devTools ??= new DevToolsPanel(this.app, this)); }
  get environmentPanel() { return (this._environmentPanel ??= new EnvironmentPanel(this.app, this)); }
  get helpTutorials() { return (this._helpTutorials ??= new HelpTutorials(this.app, this)); }
  get shareModal() { return (this._shareModal ??= new ShareModal(this.app, this)); }

  /**
   * Set the localization manager (called from main.js after CartManager init)
   */
  setLocalizationManager(localizationManager) {
    this.localization = localizationManager;
  }

  /**
   * Get translated string with optional fallback and parameters
   */
  t(key, fallback = '', params = null) {
    if (this.localization) {
      return this.localization.t(key, fallback, params);
    }
    return fallback || key;
  }

  // ===== INITIALIZATION =====
  async init() {
    // Embed mode hides all UI — skip setup, notably the per-item/per-fabric thumbnail
    // grids. Default fabric still resolves via the ET-14 spawn fallback.
    if (this.app.isEmbedMode) return;

    this.setupHamburgerMenu();
    this.setupToolbar();
    this.setupControls();
    this.setupSelectionInfoPanel();
    this.setupScrollButtons();
    this.setupBarControls();
    this.setupOverlays();
    this.setupViewToggle();
    this.setupEventListeners();

    // Initialize time controls
    this.timeControlsUI = new TimeControlsUI(this.app.getSceneManager(), this.app);
    this.timeControlsUI.init();

    await this.populateFurnitureGrid();

    await this.populateMaterialGrid();

    // Set default selected material to ET-14
    const materialManager = this.app.getMaterialManager();
    if (materialManager && !this.selectedMaterial) {
      const defaultFabricId = materialManager.getFabricIdByCode(DEFAULT_FABRIC_CODE);
      if (defaultFabricId) {
        this.selectedMaterial = defaultFabricId;
        this.fabricGrid.updateMaterialSelection();
      }
    }

    await this.environmentPanel.populateEnvironmentPanel();

    // Country/product API loads in the background; re-filter the grids in place once it lands.
    this._scheduleProductDataRefresh();

    // Show unified bar with furniture grid by default
    const unifiedBar = document.getElementById("unified-bar");
    if (unifiedBar) unifiedBar.classList.remove("hidden");

    // The dev tier of the hamburger is a function of the flag, not of which feature booted.
    if (SHOW_DEV_TOOLS) {
      document.getElementById('dev-menu')?.classList.remove('hidden');
      this.devTools.setupInfoPanel();
    }

    // Setup country selector (dev only)
    await this.devTools.setupCountrySelector();

    // Setup path tracing toggle + light rig console (dev only)
    this.devTools.setupPathTracingToggle();
    this.devTools.setupLightRigConsole();

    // Set initial view toggle state based on current mode
    const currentMode = this.app.getCameraManager().getSceneMode();
    this.updateViewToggleButtons(currentMode);

    // Set initial ceiling side based on current mode (for 2D mode, hide ceiling from above)
    const roomBuilder = this.app.getSceneManager()?.getRoomBuilder();
    if (roomBuilder) {
      roomBuilder.setCeilingSide(currentMode === "2D");
    }

    // Show room walls button only in white_room
    const currentEnv = this.app.getSceneManager()?.getCurrentEnvironment();
    this.environmentPanel.updateRoomBoxBtnVisibility(currentEnv);

    // UserPreferences.apply() may switch the style afterwards.
    this.setUIStyle(this.uiStyle);
  }

  // ===== SETUP METHODS =====
  setupHamburgerMenu() {
    const hamburgerBtn = document.getElementById("hamburger-btn");
    const drawer = document.getElementById("hamburger-drawer");
    if (!hamburgerBtn || !drawer) return;

    this._hamburgerBtn = hamburgerBtn;
    this._drawer = drawer;
    this._scrim = document.getElementById("hamburger-scrim");

    hamburgerBtn.addEventListener("click", () => this.setHamburgerOpen(!this.isHamburgerOpen()));
    document.getElementById("hamburger-close-btn")?.addEventListener("click", () => this.setHamburgerOpen(false));
    // The scrim is the drawer's outside-click target, so no document-wide listener is needed.
    this._scrim?.addEventListener("click", () => this.setHamburgerOpen(false));

    // Capture, so the drawer takes Escape before EventManager reads it as "cancel interaction".
    // Same rung as the hardware back button, so the two can never drift apart.
    document.addEventListener("keydown", (e) => {
      if (e.key !== "Escape" || !this._unwindHamburger()) return;
      e.stopPropagation();
    }, true);

    // AR is the one action that isn't universally available.
    if (arAvailable()) document.getElementById("ar-export-btn")?.classList.remove("hidden");

    // Delegated so a section added later needs no extra wiring.
    drawer.addEventListener("click", (e) => {
      const header = e.target.closest(".hamburger-section-header");
      if (!header) return;
      const section = header.closest(".hamburger-section");
      const body = section?.querySelector(".hamburger-section-body");
      if (body) this._setHamburgerSection(section, body.classList.contains("hidden"));
    });

    // Menu item handlers
    const menuActions = {
      "share-layout-btn": () => this.handleShareLayout(),
      "environment-btn": () => this.openEnvironmentOverlay(),
      "settings-btn": () => this.openSettingsModal(),
      "screenshot-btn": () => this.downloadScreenshot(),
      "history-btn": () => this.layoutPicker.openHistory(),
      "ar-export-btn": () => this.handleARExport(),
    };

    Object.entries(menuActions).forEach(([id, handler]) => {
      const btn = document.getElementById(id);
      if (btn) {
        btn.addEventListener("click", () => {
          this.setHamburgerOpen(false);
          handler();
        });
      }
    });

    // Language selector
    this.setupLanguageSelector();
  }

  isHamburgerOpen() {
    return !!this._drawer?.classList.contains("open");
  }

  /** The one way the drawer opens or closes: panel, scrim and the button's state move together. */
  setHamburgerOpen(open) {
    if (!this._drawer) return;
    this._drawer.classList.toggle("open", open);
    this._scrim?.classList.toggle("open", open);
    this._hamburgerBtn?.setAttribute("aria-expanded", String(open));
    if (open) this._syncFloorRemoveBtn();
  }

  /** Unwinds one drawer level — an open section, else the drawer. False when it was closed. */
  _unwindHamburger() {
    if (!this.isHamburgerOpen()) return false;
    const openSection = this._drawer.querySelector(".hamburger-section.open");
    if (openSection) this._collapseHamburgerSection(openSection);
    else this.setHamburgerOpen(false);
    return true;
  }

  setupLanguageSelector() {
    const container = document.getElementById("language-selector");
    const localBtn = document.getElementById("language-btn-local");
    const enBtn = document.getElementById("language-btn-en");
    if (!container || !localBtn || !enBtn || !this.localization) return;

    // Only a non-English locale loads local translations, so there is always something to toggle.
    if (!this.localization.hasLocalTranslations()) return;

    container.classList.remove("hidden");

    localBtn.textContent = this.localization.getLanguage().toUpperCase();

    const setActive = (mode) => {
      localBtn.classList.toggle("active", mode !== "en");
      enBtn.classList.toggle("active", mode === "en");
    };

    localBtn.addEventListener("click", () => {
      this.localization.switchToLocal();
      setActive("local");
    });

    enBtn.addEventListener("click", () => {
      this.localization.switchToEnglish();
      setActive("en");
    });
  }

  setupToolbar() {
    // The standalone icons in the right column.
    document.getElementById("cart-btn")?.addEventListener("click", () => this.openCart());
    document.getElementById("save-design-btn")?.addEventListener("click", () => this.saveDesignToHistory());
    document.getElementById("shortcuts-help-btn")?.addEventListener("click", () => this.showShortcutsModal());
    document.getElementById("back-to-layouts-btn")?.addEventListener("click", () => this.reopenLayoutSelectionModal());
  }

  _setHamburgerSection(section, open) {
    if (!section) return;
    section.classList.toggle('open', open);
    section.querySelector('.hamburger-section-body')?.classList.toggle('hidden', !open);
    section.querySelector('.hamburger-section-header')?.setAttribute('aria-expanded', String(open));
  }

  _collapseHamburgerSection(section) {
    this._setHamburgerSection(section, false);
  }

  /** Remove Texture is only offered when a custom floor is actually stored. */
  _syncFloorRemoveBtn() {
    const hasCustom = !!readCustomFloor()?.dataURL;
    document.getElementById('floor-remove-btn')?.classList.toggle('hidden', !hasCustom);
  }

  // ===== UI STYLE (Layout 1 / Layout 2) =====

  /**
   * Switch between the classic bar (layout1) and the docked drawer (layout2).
   *
   * The two layouts share one copy of the product options and one selection label —
   * this re-parents them rather than duplicating the markup, which is what keeps every
   * handler bound in setupControls()/setupSelectionInfoPanel() working in both.
   * Idempotent: it is called both from the settings toggle and from
   * UserPreferences.apply() at boot.
   *
   * @param {'layout1'|'layout2'} style
   */
  setUIStyle(style) {
    const next = style === 'layout2' ? 'layout2' : 'layout1';
    this.uiStyle = next;
    document.body.dataset.uiStyle = next;

    const selection = document.getElementById('selection-label');
    const isDrawer = next === 'layout2';

    // Layout 1 puts the label back where index.html declared it: floating over the canvas.
    const selectionHost = isDrawer
      ? document.getElementById('drawer-selection-host')
      : document.getElementById('app');
    if (selection && selectionHost && selection.parentElement !== selectionHost) {
      selectionHost.appendChild(selection);
    }

    document.getElementById('drawer-header')?.classList.toggle('hidden', !isDrawer);

    document.querySelectorAll('.ui-style-opt').forEach((btn) => {
      btn.classList.toggle('active', btn.dataset.value === next);
    });

    this._syncDrawerHosts();
    this._setBarGrid(this.activeTab);
    this._syncCanvasInset();
  }

  /**
   * Handle follows whether the header is rendered; options follow the selection (with
   * nothing selected they return to the hamburger, where these scene-wide settings must
   * stay reachable). Re-parented, not duplicated, so setupControls() listeners survive.
   * @param {Array} [selectedOverride] the selection updateSelection is mid-way through applying.
   */
  _syncDrawerHosts(selectedOverride) {
    const selected = selectedOverride
      ?? this.app.getFurnitureManager()?.getSelectedFurnitureArray()
      ?? [];
    const isDrawer = this.uiStyle === 'layout2';
    const headerRendered = isDrawer && !this.barCollapsed;
    const optionsInHeader = headerRendered && selected.length > 0;

    const options = document.getElementById('product-options');
    const optionsHost = document.getElementById(optionsInHeader ? 'drawer-header' : 'product-options-host');
    if (options && optionsHost && options.parentElement !== optionsHost) {
      optionsHost.appendChild(options);
    }
    document.getElementById('product-options-section')?.classList.toggle('hidden', optionsInHeader);

    const handle = document.getElementById('bar-drag-handle');
    const handleHost = document.getElementById(
      headerRendered ? 'drawer-header' : (isDrawer ? 'unified-bar' : 'bottom-ui')
    );
    if (handle && handleHost && handle.parentElement !== handleHost) {
      handleHost.insertBefore(handle, handleHost.firstChild);
    }
  }

  /**
   * Element grid or fabric grid — follows the selection, not a mode the user picks.
   * @param {'furniture'|'material'} grid
   */
  _setBarGrid(grid) {
    this.activeTab = grid;

    const furnitureGrid = document.getElementById('furniture-grid');
    const materialGrid = document.getElementById('material-grid');
    if (furnitureGrid) furnitureGrid.classList.toggle('bar-grid-hidden', grid !== 'furniture');
    if (materialGrid) materialGrid.classList.toggle('bar-grid-hidden', grid !== 'material');

    // The two grids are different widths, so which arrows apply changes with them.
    this._updateBarScrollButtons();
  }

  /** Drawer header line: catalog dimensions only — the title row above carries the name. */
  _updateDrawerInfo(furnitureArray) {
    const info = document.getElementById('drawer-info');
    if (!info) return;

    const list = furnitureArray || [];
    if (list.length !== 1) {
      info.textContent = '';
      info.classList.add('hidden');
      return;
    }

    const productId = activeProductCode(list[0]);
    info.textContent = productId ? this._productDimensionsText(productId) : '';
    info.classList.toggle('hidden', !info.textContent);
  }

  updateCartButtonVisibility(isAvailable) {
    const cartButton = document.getElementById('cart-btn');
    if (cartButton) {
      cartButton.style.display = isAvailable ? '' : 'none';
    }
  }

  setupSelectionInfoPanel() {
    // Setup selected furniture action buttons
    const rotateBtn = document.getElementById("selected-rotate-btn");
    const deleteBtn = document.getElementById("selected-delete-btn");

    if (rotateBtn) {
      rotateBtn.addEventListener("click", () => {
        const furnitureManager = this.app.getFurnitureManager();
        const eventManager = this.app.getEventManager();
        const step = eventManager?.rotationStep ?? Math.PI / 2;
        const selectedArray = furnitureManager?.getSelectedFurnitureArray() || [];
        if (selectedArray.length > 1) {
          this.rotateGroupAroundCenter(selectedArray, step);
          furnitureManager?.updateSelectionOutline();
          this.app.getStateManager()?.saveSceneState();
        } else {
          const selectedFurniture = furnitureManager?.getSelectedFurniture();
          if (!selectedFurniture) return;
          // Modules and floor-standing accessories rotate; anything on an AP doesn't.
          if (!isFreeStanding(selectedFurniture)) return;
          eventManager?.rotateModuleAndResnap(selectedFurniture, step);
          furnitureManager?.updateSelectionOutline();
          this.app.getStateManager()?.saveSceneState();
        }
      });
    }

    const variantBtn = document.getElementById("selected-variant-btn");
    if (variantBtn) {
      variantBtn.addEventListener("click", () => {
        const furnitureManager = this.app.getFurnitureManager();
        const selectedFurniture = furnitureManager?.getSelectedFurniture();
        if (!selectedFurniture) return;
        furnitureManager.switchFurnitureVariant(selectedFurniture);
        this.updateVariantButton(selectedFurniture);
        // A variant may be its own product (clooods-cx), with its own name and dimensions.
        this._renderSelectionInfo(selectedFurniture);
      });
    }

    if (deleteBtn) {
      deleteBtn.addEventListener("click", () => {
        if (this.app.getFurnitureManager()?.deleteSelected()) this.updateSelection(null);
      });
    }
  }

  /** The room the drawer occupies once settled — the fold is a display swap, so already final. */
  _canvasInsetBottom() {
    const bottomUi = document.getElementById('bottom-ui');
    if (this.uiStyle !== 'layout2' || !bottomUi) return 0;
    return Math.round(Math.max(0, window.innerHeight - bottomUi.getBoundingClientRect().top));
  }

  // Inset the canvas by the drawer's height so opening it reveals the controls rather than
  // covering the sofa. Layout 1's bar keeps floating over the scene.
  _syncCanvasInset() {
    const bottom = this._canvasInsetBottom();

    // Resizing a WebGL framebuffer is not free, so only when the value really moved.
    if (bottom === this._insetBottom) return;
    this._insetBottom = bottom;
    document.body.style.setProperty('--canvas-inset-bottom', `${bottom}px`);
    this.app.getEventManager()?.onWindowResize?.();
  }

  /**
   * Which element actually scrolls the item rows sideways. Layout 1 scrolls the whole bar;
   * Layout 2 can't — the drawer header and tab strip live inside it and would slide off
   * with the grid, so there the scroll host is the grid container alone.
   */

  _barScrollHost() {
    return document.getElementById(
      this.uiStyle === 'layout2' ? 'bar-scroll-container' : 'unified-bar'
    );
  }

  setupScrollButtons() {
    const scrollLeft = document.getElementById("bar-scroll-left-btn");
    const scrollRight = document.getElementById("bar-scroll-right-btn");

    if (scrollLeft) {
      scrollLeft.addEventListener("pointerdown", (e) => {
        e.stopPropagation();
        this._barScrollHost()?.scrollBy({ left: -200, behavior: "smooth" });
      });
    }
    if (scrollRight) {
      scrollRight.addEventListener("pointerdown", (e) => {
        e.stopPropagation();
        this._barScrollHost()?.scrollBy({ left: 200, behavior: "smooth" });
      });
    }

    // Both candidates are listened to — only one is ever the real scroll host per layout.
    ["unified-bar", "bar-scroll-container"].forEach((id) => {
      document.getElementById(id)
        ?.addEventListener("scroll", () => this._updateBarScrollButtons(), { passive: true });
    });
    this._updateBarScrollButtons();
  }

  /** An arrow only shows while there is something in that direction to reach. */
  _updateBarScrollButtons() {
    const host = this._barScrollHost();
    const left = document.getElementById("bar-scroll-left-btn");
    const right = document.getElementById("bar-scroll-right-btn");
    if (!host || !left || !right) return;

    // Sub-pixel layout rounding leaves a fractional overflow on a row that visibly fits.
    const overflow = host.scrollWidth - host.clientWidth > 2;
    const at = Math.round(host.scrollLeft);
    left.classList.toggle("hidden", !overflow || at <= 0);
    right.classList.toggle("hidden", !overflow || at >= host.scrollWidth - host.clientWidth - 2);
  }

  setupBarControls() {
    const dragHandle = document.getElementById("bar-drag-handle");
    const selectAllBtn = document.getElementById("bar-select-all-btn");

    if (dragHandle) {
      let start = 0;
      let hasMoved = false;

      const onStart = (v) => {
        start = v;
        hasMoved = false;
      };

      const onMove = (v) => {
        if (Math.abs(v - start) > 8) hasMoved = true;
      };

      // A tap toggles; a drag only toggles when it travels away from the current state.
      const onEnd = (v) => {
        const away = v - start;
        if (!hasMoved || (Math.abs(away) > 30 && (away > 0) !== this.barCollapsed)) this.toggleBar();
      };

      // `guard` lets a surface carry the gesture without stealing its own controls' taps.
      const wireDrag = (el, guard) => {
        el.addEventListener("pointerdown", (e) => {
          if (guard && !guard(e)) return;
          e.preventDefault(); // prevents magnifier, selection loupe, and callout — not scroll-blocking so no passive warning
          el.setPointerCapture(e.pointerId);
          onStart(e.clientY);
        });
        el.addEventListener("pointermove", (e) => {
          if (!el.hasPointerCapture(e.pointerId)) return;
          onMove(e.clientY);
        });
        el.addEventListener("pointerup", (e) => {
          if (!el.hasPointerCapture(e.pointerId)) return;
          el.releasePointerCapture(e.pointerId);
          onEnd(e.clientY);
        });
        el.addEventListener("pointercancel", () => {
          start = 0;
          hasMoved = false;
        });
      };

      wireDrag(dragHandle);

      // The header carries the gesture (the pill is only 28px), minus its own controls and
      // the handle — otherwise both wirings fight over the same pointer capture.
      const header = document.getElementById("drawer-header");
      if (header) {
        wireDrag(header, (e) => !e.target.closest("button, select, input, label, a, #bar-drag-handle"));
      }
    }

    if (selectAllBtn) {
      selectAllBtn.addEventListener("click", () => this.selectAllFurniture());
    }
  }

  toggleBar() {
    this.barCollapsed = !this.barCollapsed;
    const unifiedBar = document.getElementById("unified-bar");
    const bottomUi = document.getElementById("bottom-ui");

    if (unifiedBar) {
      unifiedBar.classList.toggle("collapsed", this.barCollapsed);
    }
    if (bottomUi) {
      bottomUi.classList.toggle("bar-collapsed", this.barCollapsed);
    }
    // Collapsing hides the header, so the handle has to come out of it first — otherwise
    // the only control that reopens the sheet is inside the thing that just closed.
    this._syncDrawerHosts();
    this._syncCanvasInset();
  }

  selectAllFurniture() {
    const furnitureManager = this.app.getFurnitureManager();
    if (!furnitureManager) return;

    const modules = this.selectAllTargets();
    if (modules.length === 0) return;

    // Toggle: if all modules are already selected, deselect all
    const selected = furnitureManager.getSelectedFurnitureArray();
    if (selected.length === modules.length) {
      furnitureManager.selectFurniture(null);
      this.updateSelection([]);
      return;
    }

    // Otherwise select all modules, with their components: Select All means everything.
    furnitureManager.selectFurniture(null);
    for (const furniture of modules) {
      furnitureManager.selectFurniture(furniture, true, { withComponents: true });
    }

    this.updateSelection(furnitureManager.getSelectedFurnitureArray());
  }

  _syncRoomBoxControls(roomBox) {
    for (const axis of ["width", "depth"]) {
      const slider = document.getElementById(`room-box-${axis}-slider`);
      if (slider) slider.value = roomBox[axis];
      const label = document.getElementById(`room-box-${axis}-value`);
      if (label) label.textContent = roomBox[axis].toFixed(1) + " m";
    }
  }

  // Leg colour shows unless the collection opts out of leg textures; leg height needs the
  // collection to declare hasLegs AND the API to list heights for it.
  _syncLegOptions(collectionId) {
    const furnitureManager = this.app.getFurnitureManager();
    const collection = furnitureManager?.getFurnitureCollections()?.[collectionId];
    document.getElementById('leg-color-option')
      ?.classList.toggle('hidden', collection?.legTextures === false);
    const hasLegHeights = furnitureManager?.legHeightsData?.collection?.[`${collectionId}-collection`]?.length > 0;
    document.getElementById('leg-height-option')
      ?.classList.toggle('hidden', !(collection?.hasLegs === true && hasLegHeights));
  }

  // What Select All picks — base modules only, attachments ride along — and never zip. The
  // rotate button and the R key both compare a selection against this to detect select-all.
  selectAllTargets() {
    return (this.app.getFurnitureManager()?.getAllFurniture() || []).filter(f => {
      if (f.furnitureData?.collection === 'zip') return false;
      const componentType = f.furnitureData?.componentType;
      return !componentType || componentType === 'module';
    });
  }

  updateSelectAllButton() {
    const selectAllBtn = document.getElementById("bar-select-all-btn");
    if (!selectAllBtn) return;

    const furnitureManager = this.app.getFurnitureManager();
    if (!furnitureManager) return;

    const modules = this.selectAllTargets();
    const selected = furnitureManager.getSelectedFurnitureArray();

    const allSelected = modules.length > 0 && selected.length === modules.length;
    selectAllBtn.classList.toggle("active", allSelected);
    // The .active styling is the only thing a sighted user sees; aria-pressed is its equivalent.
    selectAllBtn.setAttribute("aria-pressed", String(allSelected));
  }

  rotateGroupAroundCenter(furnitureArray, angle) {
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);

    // Expand to full connected group(s) so unselected neighbours (e.g. zip) aren't torn off.
    const moduleSet = new Set();
    for (const f of furnitureArray) {
      if (f.furnitureData?.componentType === 'module' && f.getAllGroupModules) {
        for (const m of f.getAllGroupModules()) moduleSet.add(m);
      } else {
        moduleSet.add(f);
      }
    }
    const targets = [...moduleSet];
    if (targets.length === 0) return;

    // Refresh full rotation-aware boxes up front (a dragged off-origin piece can carry a stale
    // center that would scatter the group) and accumulate the visual-center centroid.
    let cx = 0, cz = 0;
    for (const f of targets) {
      f.computeBoundingBoxFull();
      cx += f.boundingBox.center.x;
      cz += f.boundingBox.center.z;
    }
    cx /= targets.length;
    cz /= targets.length;

    // Orbit each visual center about the world origin: recentres and rotates the group in one rigid map.
    for (const f of targets) {
      const dx = f.boundingBox.center.x - cx;
      const dz = f.boundingBox.center.z - cz;
      const newVcX = cos * dx + sin * dz;
      const newVcZ = -sin * dx + cos * dz;
      f.mesh.rotateY(angle);
      f.computeBoundingBoxFull();
      f.setCenterXZ(newVcX, newVcZ);
    }

    // Components rode along as scene-graph children — refresh their world boxes so later reads aren't stale.
    for (const f of targets) {
      for (const child of f.attachedChildren) {
        if (child.furnitureData?.componentType !== 'module') child.computeBoundingBoxFull?.();
      }
    }

    // Geometry moved — refresh on-demand shadows.
    this.app.getSceneManager()?.requestShadowUpdate();
  }

  setupOverlays() {
    // Settings overlay close
    const settingsClose = document.getElementById("settings-overlay-close");
    if (settingsClose) {
      settingsClose.addEventListener("click", () => {
        this.closeOverlay("settings");
      });
    }

    // Environment overlay close
    const envClose = document.getElementById("environment-overlay-close");
    if (envClose) {
      envClose.addEventListener("click", () => {
        this.closeOverlay("environment");
      });
    }

    // Close overlays when clicking outside (on the backdrop)
    const settingsOverlay = document.getElementById("settings-overlay");
    if (settingsOverlay) {
      settingsOverlay.addEventListener("click", (e) => {
        if (e.target === settingsOverlay) this.closeOverlay("settings");
      });
    }

    const envOverlay = document.getElementById("environment-overlay");
    if (envOverlay) {
      envOverlay.addEventListener("click", (e) => {
        if (e.target === envOverlay) this.closeOverlay("environment");
      });
    }
  }

  setupViewToggle() {
    document.getElementById("view-toggle-btn")?.addEventListener("click", () => {
      // Deselect furniture when switching modes
      if (this.app.getFurnitureManager()?.getSelectedFurniture()) {
        this.app.getFurnitureManager()?.selectFurniture(null);
        this.updateSelection(null);
      }

      this.app.getCameraManager().toggleSceneMode(
        () => {
          this.onSceneModeTransitionStart();
        },
        (mode) => {
          this.onSceneModeChanged(mode);
        },
      );
    });
  }

  updateViewToggleButtons(mode) {
    const btn = document.getElementById("view-toggle-btn");
    if (btn) btn.textContent = mode === "2D" ? "3D" : "2D";
  }

  setupControls() {
    this._setupProductOptionControls();
    this._setupViewControls();
    this.devTools.setupDevToggles();
    this.devTools.setupIblPopover();
    this._setupFloorTextureControls();
    this._setupRoomBoxControls();
    this._setupUnitAndShadowControls();
    this._setupModalControls();
  }

  _setupProductOptionControls() {
    // #product-options is re-parented by setUIStyle(), which preserves these listeners.
    document.getElementById('leg-height-select-inline')?.addEventListener('change', (e) => {
      this.changeLegHeight(e.target.value);
      this.app.getStateManager()?.saveSceneState();
    });

    document.querySelectorAll('.leg-color-opt').forEach((btn) => {
      btn.addEventListener('click', () => {
        this.changeLegTexture(btn.dataset.value);
        this.app.getStateManager()?.saveSceneState();
      });
    });

    document.querySelectorAll('.wood-finish-opt').forEach((btn) => {
      btn.addEventListener('click', () => {
        this.changeWoodTexture(btn.dataset.value);
        this.app.getStateManager()?.saveSceneState();
      });
    });

    // UI style (Layout 1 / Layout 2)
    document.querySelectorAll('.ui-style-opt').forEach((btn) => {
      btn.addEventListener('click', () => {
        this.setUIStyle(btn.dataset.value);
        this.app.getStateManager()?.saveSceneState();
      });
    });
  }

  _setupViewControls() {
    // Dimensions toggle
    const dimensionsToggle = document.getElementById("dimensions-toggle");
    if (dimensionsToggle) {
      dimensionsToggle.addEventListener("change", (e) => {
        const dimensionManager = this.app.getDimensionManager();
        if (dimensionManager) {
          dimensionManager.setEnabled(e.target.checked);
        }
        // Save state after toggling dimensions
        const stateManager = this.app.getStateManager();
        if (stateManager) {
          stateManager.saveSceneState();
        }
      });
    }

    // Centre now, or switching it on appears to do nothing until the next move.
    document.getElementById("auto-center-toggle")?.addEventListener("change", (e) => {
      const furnitureManager = this.app.getFurnitureManager();
      furnitureManager?.setAutoCenter(e.target.checked);
      if (e.target.checked) furnitureManager?.centerFurnitureInScene();
      this.app.getStateManager()?.saveSceneState();
    });

    // White floor toggle (live — applies to the scene, not just the screenshot)
    this._whiteFloorToggle()?.addEventListener("change", (e) => {
      this.app.getSceneManager()?.getRoomBuilder()?.setWhiteFloor(e.target.checked);
      this.app.getStateManager()?.saveSceneState();
    });

    // Grid floor toggle
    const gridFloorToggle = document.getElementById("grid-floor-toggle");
    if (gridFloorToggle) {
      gridFloorToggle.addEventListener("change", (e) => {
        const roomBuilder = this.app.getSceneManager()?.getRoomBuilder();
        if (roomBuilder) {
          roomBuilder.toggleGridOverlay(e.target.checked);
        }
      });
    }

    // Snapping toggle — enables/disables module-to-module snapping
    const snappingToggle = document.getElementById("snapping-toggle");
    if (snappingToggle) {
      snappingToggle.addEventListener("change", (e) => {
        const eventManager = this.app.getEventManager();
        if (eventManager) eventManager.snappingEnabled = e.target.checked;
        this.app.getStateManager()?.saveSceneState();
      });
    }

    // Panning toggle — enables/disables panning in 3D orbit mode
    const panningToggle = document.getElementById("panning-toggle");
    if (panningToggle) {
      panningToggle.addEventListener("change", (e) => {
        const cameraManager = this.app.getCameraManager();
        if (cameraManager) cameraManager.set3DPanningEnabled(e.target.checked);
        this.app.getStateManager()?.saveSceneState();
      });
    }
  }

  _setupFloorTextureControls() {
    // Seeds it; the hamburger's open handler re-checks.
    this._syncFloorRemoveBtn();


    // "Upload Image" action
    document.getElementById("floor-upload-trigger")?.addEventListener("click", () => {
      document.getElementById("floor-texture-input")?.click();
    });

    // File selected → open crop modal
    this.floorCropModal = new FloorCropModal(async (croppedDataURL, realW, realH) => {
      const roomBuilder = this.app.getSceneManager()?.getRoomBuilder();
      if (roomBuilder) {
        if (realW > 0 && realH > 0) roomBuilder.changeRoomSize(realW, realH);
        roomBuilder.applyCustomFloorTexture(croppedDataURL);
        try {
          // Natively the image becomes a file and only its URL is recorded — a 2-3MB
          // base64 string in localStorage is the app's biggest quota risk, and it would
          // be re-serialised into every saveSceneState().
          const src = await saveFloorImage(croppedDataURL);
          saveCustomFloor({ src, realW, realH });
        } catch (err) { console.warn("Could not save custom floor texture:", err); }
        this.app.getStateManager()?.saveSceneState();
      }
      this._syncFloorRemoveBtn();
    }, (key, fallback) => this.t(key, fallback));

    const floorTextureInput = document.getElementById("floor-texture-input");
    floorTextureInput?.addEventListener("change", (e) => {
      const file = e.target.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (evt) => this.floorCropModal.open(evt.target.result);
      reader.readAsDataURL(file);
      e.target.value = "";
    });

    // "Remove Texture" action
    document.getElementById("floor-remove-btn")?.addEventListener("click", () => {
      const roomBuilder = this.app.getSceneManager()?.getRoomBuilder();
      roomBuilder?.clearCustomFloorTexture();
      roomBuilder?.changeRoomSize(10, 10);
      clearCustomFloor();
      this.app.getStateManager()?.saveSceneState();
      this._syncFloorRemoveBtn();
    });
  }

  _setupRoomBoxControls() {
    // Room box toggle
    const roomBoxToggle = document.getElementById("room-box-toggle");
    if (roomBoxToggle) {
      roomBoxToggle.addEventListener("change", (e) => {
        const roomBox = this.app.getSceneManager()?.getRoomBox();
        if (!roomBox) return;
        const sizeControls = document.getElementById("room-box-size-controls");
        if (e.target.checked) {
          // Center furniture then build walls at default 6×8 m (clamped up if furniture is larger)
          const fm = this.app.getFurnitureManager();
          fm?.centerFurnitureInScene();
          roomBox.setSize(6, 8, fm);
          roomBox.show();
          this._syncRoomBoxControls(roomBox);
          if (sizeControls) sizeControls.classList.remove("hidden");
        } else {
          roomBox.hide();
          if (sizeControls) sizeControls.classList.add("hidden");
        }
        this.app.getStateManager()?.saveSceneState();
      });
    }

    // Room box width / depth sliders. setSize clamps to what fits the furniture, so the
    // controls are re-synced from the box rather than left at the dragged value.
    for (const axis of ["width", "depth"]) {
      document.getElementById(`room-box-${axis}-slider`)?.addEventListener("input", (e) => {
        const roomBox = this.app.getSceneManager()?.getRoomBox();
        if (!roomBox) return;
        const val = parseFloat(e.target.value);
        roomBox.setSize(axis === "width" ? val : roomBox.width, axis === "depth" ? val : roomBox.depth,
          this.app.getFurnitureManager());
        this._syncRoomBoxControls(roomBox);
        this.app.getStateManager()?.saveSceneState();
      });
    }
  }

  _setupUnitAndShadowControls() {
    // Dimension unit selector
    const dimensionUnitSelect = document.getElementById("dimension-unit-select");
    if (dimensionUnitSelect) {
      dimensionUnitSelect.addEventListener("change", (e) => {
        const dimensionManager = this.app.getDimensionManager();
        if (dimensionManager) {
          dimensionManager.setUnit(e.target.value);
        }
        // Save state after changing dimension unit
        const stateManager = this.app.getStateManager();
        if (stateManager) {
          stateManager.saveSceneState();
        }
      });
    }

    // Shadow quality selector
    const shadowQualitySelect = document.getElementById("shadow-quality-select");
    if (shadowQualitySelect) {
      // Markup carries no `selected` (the default is per-platform); a stored preference syncs over it later.
      this.syncStateToUI({ shadowQuality: this.app.getSceneManager()?.shadowQuality });

      shadowQualitySelect.addEventListener("change", (e) => {
        const sceneManager = this.app.getSceneManager();
        if (sceneManager) {
          sceneManager.setShadowQuality(e.target.value);
        }
        // Save state after changing shadow quality
        const stateManager = this.app.getStateManager();
        if (stateManager) {
          stateManager.saveSceneState();
        }
      });
    }
  }

  _setupModalControls() {
    // Share modal controls
    const copyUrlBtn = document.getElementById("copy-url-btn");
    if (copyUrlBtn) {
      copyUrlBtn.addEventListener("click", () => this.copyUrlToClipboard());
    }

    document.getElementById("native-share-btn")?.addEventListener("click", () => this.shareLinkViaSheet());

    for (const closeBtn of document.querySelectorAll("#share-modal .share-close-btn")) {
      closeBtn.addEventListener("click", () => this.closeShareModal());
    }

    // Close modal when clicking outside
    const shareModal = document.getElementById("share-modal");
    if (shareModal) {
      shareModal.addEventListener("click", (e) => {
        if (e.target === shareModal) {
          this.closeShareModal();
        }
      });
    }

    // Shortcuts modal controls
    const closeShortcutsBtn = document.getElementById("close-shortcuts-modal-btn");
    if (closeShortcutsBtn) {
      closeShortcutsBtn.addEventListener("click", () => this.closeShortcutsModal());
    }

    // Close shortcuts modal when clicking outside
    const shortcutsModal = document.getElementById("shortcuts-modal");
    if (shortcutsModal) {
      shortcutsModal.addEventListener("click", (e) => {
        if (e.target === shortcutsModal) {
          this.closeShortcutsModal();
        }
      });
    }
  }

  setupEventListeners() {
    // Simple event listeners - keep only window resize
    window.addEventListener("resize", () => {
      this.handleResize();
    });
  }

  // ===== UI POPULATION =====
  async populateFurnitureGrid() {
    try {
      const furnitureManager = this.app.getFurnitureManager();
      if (!furnitureManager) {
        console.warn("Furniture manager not available");
        return;
      }

      const collections = furnitureManager.getFurnitureCollections();
      if (!collections || Object.keys(collections).length === 0) {
        console.warn("No furniture collections available");
        return;
      }

      // Load component categories from furniture data
      await this.loadComponentCategories();

      // Set default category if not set
      if (!this.currentCategory && Object.keys(collections).length > 0) {
        this.currentCategory = Object.keys(collections)[0];
      }

      if (this.currentCategory) this._syncLegOptions(this.currentCategory);

      // Covers button visibility + rows are driven by what's in scene
      this.updateCoversUI();

      await this.furnitureGrid.updateFurnitureGrid();
    } catch (error) {
      console.error("Error populating furniture grid:", error);
    }
  }

  async loadComponentCategories() {
    try {
      const response = await fetchAsset("data/furniture.json");
      if (!response.ok) return;
      const data = await response.json();
      this.componentCategories = data.componentCategories || {};
    } catch (error) {
      console.warn("Could not load component categories:", error);
    }
  }

  async populateMaterialGrid() {
    const materialManager = this.app.getMaterialManager();
    if (!materialManager) return;

    await this.fabricGrid.updateMaterialGrid();
  }

  // Re-filter the grids once the background product data lands; fire-and-forget, idempotent.
  // updateMaterialGrid self-skips via its fingerprint when filtering didn't change the fabric set.
  _scheduleProductDataRefresh() {
    const fm = this.app.getFurnitureManager?.();
    const mm = this.app.getMaterialManager?.();
    const pending = [fm?.productDataReady, mm?.productDataReady].filter(Boolean);
    if (!pending.length) return;
    Promise.all(pending)
      .then(() => {
        this.fabricGrid.updateMaterialGrid();
        this.populateFurnitureGrid();
      })
      .catch(() => {});
  }

  // Scroll the bar to the first element matching `selector`: centred, or up against the left edge.
  _scrollBarTo(selector, align) {
    const bar = this._barScrollHost();
    const el = bar?.querySelector(selector);
    if (!el) return;

    const barRect = bar.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    const inset = align === 'center' ? barRect.width / 2 - elRect.width / 2 : 12;
    const offset = elRect.left - barRect.left + bar.scrollLeft - inset;

    bar.scrollTo({ left: Math.max(0, offset), behavior: 'smooth' });
  }

  // ===== ENVIRONMENT PANEL =====
  _whiteFloorToggle() {
    return document.getElementById("white-floor-screenshot-toggle");
  }

  /** Reflect the live RoomBuilder override into the checkbox (setWhiteFloor no-ops without a floor). */
  _syncWhiteFloorToggle() {
    const toggle = this._whiteFloorToggle();
    if (toggle) toggle.checked = !!this.app.getSceneManager()?.getRoomBuilder()?.whiteFloorEnabled;
  }

  changeLegTexture(legType) {
    const furnitureManager = this.app.getFurnitureManager();
    if (furnitureManager) {
      furnitureManager.updateAllLegTextures(legType);
    }
    // Sync inline color buttons
    document.querySelectorAll('.leg-color-opt').forEach((btn) => {
      btn.classList.toggle('active', btn.dataset.value === legType);
    });
  }

  // Apply the wood finish to the currently selected wood-surface element(s).
  // Per-element: each oiko-sta keeps its own light/dark finish.
  changeWoodTexture(woodType) {
    const furnitureManager = this.app.getFurnitureManager();
    if (furnitureManager) {
      const selected = furnitureManager.getSelectedFurnitureArray?.() || [];
      selected.forEach((el) => {
        if (furnitureManager.isWoodSurfaceElement(el)) {
          furnitureManager.setElementWoodType(el, woodType);
        }
      });
    }
    document.querySelectorAll('.wood-finish-opt').forEach((btn) => {
      btn.classList.toggle('active', btn.dataset.value === woodType);
    });
  }

  // Show the Wood Finish button only while the selection contains a dedicated
  // wood surface (e.g. the oiko-sta side table), and reflect that element's
  // current finish. Called from updateSelection.
  updateWoodFinishButton(furnitureArray) {
    const option = document.getElementById('wood-finish-option');
    if (!option) return;
    const furnitureManager = this.app.getFurnitureManager();
    const woodElements = (furnitureArray || []).filter(
      (el) => furnitureManager?.isWoodSurfaceElement(el)
    );
    const hasWood = woodElements.length > 0;
    option.classList.toggle('hidden', !hasWood);
    if (!hasWood) return;
    // Reflect the first wood element's finish in the button state.
    const activeType = woodElements[0].woodType || DEFAULT_WOOD_TYPE;
    document.querySelectorAll('.wood-finish-opt').forEach((opt) => {
      opt.classList.toggle('active', opt.dataset.value === activeType);
    });
  }

  changeLegHeight(legHeight) {
    const furnitureManager = this.app.getFurnitureManager();
    if (furnitureManager) {
      furnitureManager.applyLegHeightToAll(legHeight, { animate: true });
    }
    // Sync inline select
    const legHeightSelectInline = document.getElementById('leg-height-select-inline');
    if (legHeightSelectInline && legHeightSelectInline.value !== legHeight) {
      legHeightSelectInline.value = legHeight;
    }
  }

  changeCovers(group, value) {
    const furnitureManager = this.app.getFurnitureManager();
    if (furnitureManager) {
      furnitureManager.currentCovers[group] = value;
    }
    // Sync active state within this group only
    document.querySelectorAll(`.covers-opt[data-group="${group}"]`).forEach((btn) => {
      btn.classList.toggle('active', btn.dataset.value === value);
    });
  }

  /**
   * Name a covers row for the categories it governs rather than for its group key. The keys
   * (`module` / `backrest`) are a cover-type family persisted in saved layouts, not a component
   * type: the `backrest` group holds choice back panels *and* TA-1/TA-2 ottomans, so any fixed
   * word is wrong for one of them. Catalog-authored, like the grid's own group names.
   * @param {Set<string>} componentTypes
   * @returns {string} e.g. "Panels", "Panels & Accessories"
   */
  coverGroupLabel(componentTypes) {
    return [...componentTypes]
      .sort((a, b) => (this.componentCategories[a]?.order ?? 999) -
                      (this.componentCategories[b]?.order ?? 999))
      .map((type) => this.componentCategories[type]?.name || type)
      .join(' & ');
  }

  // Show/rebuild the covers rows from in-scene hasCovers items, grouped by product type.
  updateCoversUI() {
    const coversOption = document.getElementById('covers-option');
    const coversRows = document.getElementById('covers-rows');
    if (!coversOption || !coversRows) return;

    const fm = this.app.getFurnitureManager();
    if (!fm) return;
    const collections = fm.getFurnitureCollections?.() || {};

    // group key -> { options: Set, types: Set, repCode: string } collected across in-scene items
    const groups = {};
    for (const f of fm.getAllFurniture()) {
      const data = f.furnitureData;
      if (!data || collections[data.collection]?.hasCovers !== true) continue;
      const code = activeProductCode(f);
      const options = fm.getCoverOptions(code);
      if (options.length === 0) continue;
      const group = fm.getCoverGroup(code);
      if (!group) continue;
      if (!groups[group]) groups[group] = { options: new Set(), types: new Set(), repCode: code };
      options.forEach((o) => groups[group].options.add(o));
      groups[group].types.add(data.componentType || 'module');
    }

    const presentGroups = ['module', 'backrest'].filter((g) => groups[g]);

    coversOption.classList.toggle('hidden', presentGroups.length === 0);
    if (presentGroups.length === 0) {
      coversRows.innerHTML = '';
      return;
    }

    let html = `<span class="option-label" data-i18n="covers">${this.t('covers', 'Covers')}</span>`;
    for (const group of presentGroups) {
      const options = Array.from(groups[group].options);
      // Seed/repair the selection so a button shows active (default for the country).
      if (!options.includes(fm.currentCovers[group])) {
        fm.currentCovers[group] = fm.getDefaultCover(groups[group].repCode);
      }
      const selected = fm.currentCovers[group];
      const btns = options
        .map(
          (opt) =>
            `<button class="covers-opt${opt === selected ? ' active' : ''}" data-group="${group}" data-value="${opt}" data-i18n="${opt}">${this.t(opt, opt)}</button>`
        )
        .join('');
      const label = this.coverGroupLabel(groups[group].types);
      html += `<div class="covers-group"><span class="covers-group-label">${label}</span><div class="option-btns">${btns}</div></div>`;
    }
    coversRows.innerHTML = html;

    coversRows.querySelectorAll('.covers-opt').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.changeCovers(btn.dataset.group, btn.dataset.value);
        this.app.getStateManager()?.saveSceneState();
      });
    });
  }

  // ===== ONBOARDING HINT =====
  showDragHint() {
    const STORAGE_KEY = 'feydom_drag_hint_shown';
    if (localStorage.getItem(STORAGE_KEY)) return;
    localStorage.setItem(STORAGE_KEY, '1');

    const hint = document.getElementById('drag-hint');
    if (!hint) return;

    // Show after a short delay so the scene settles first
    setTimeout(() => {
      hint.classList.remove('hidden');

      const dismiss = () => {
        hint.classList.add('hint-exit');
        hint.addEventListener('animationend', () => hint.remove(), { once: true });
        document.removeEventListener('pointerdown', onInteract, { capture: true });
      };

      // Dismiss on close button
      document.getElementById('drag-hint-close')?.addEventListener('click', dismiss);

      // Dismiss when the user starts their first real drag
      const onInteract = (e) => {
        if (e.target?.closest('#drag-hint')) return; // ignore clicks inside hint
        dismiss();
      };
      document.addEventListener('pointerdown', onInteract, { capture: true, once: true });

      // Auto-dismiss after 7s
      setTimeout(dismiss, 7000);
    }, 1200);
  }

  // ===== EVENT HANDLERS =====
  startBarItemDrag(furniture, initialEvent) {
    const canvas = this.app.getRenderer().domElement;
    let ghostCreated = false;
    let ghostCreating = false;
    // Prevent double-handling on devices that fire both touch and pointer events
    let activeSource = null; // 'touch' | 'pointer'
    // dragCommitted: true once the pointer has moved upward toward the canvas
    // (distinguishes drag intent from horizontal bar scroll)
    let dragCommitted = false;

    const startX = initialEvent?.clientX ?? 0;
    const startY = initialEvent?.clientY ?? 0;

    // ── Visual feedback ──────────────────────────────────────────────
    const barItem = document.querySelector(`[data-furniture-id="${furniture.id}"]`);
    // Don't add drop-active or dragging class until drag is committed

    // Floating drag preview that follows the cursor
    const dragPreview = document.createElement('div');
    dragPreview.className = 'drag-preview';
    dragPreview.style.opacity = '0'; // hidden until committed
    const previewImg = barItem?.querySelector('.furniture-thumbnail-image');
    if (previewImg) {
      const img = document.createElement('img');
      img.src = previewImg.src;
      img.className = 'drag-preview-img';
      img.draggable = false;
      dragPreview.appendChild(img);
    }
    const previewLabel = document.createElement('span');
    previewLabel.className = 'drag-preview-label';
    previewLabel.textContent = furniture.name;
    dragPreview.appendChild(previewLabel);
    document.body.appendChild(dragPreview);

    const moveDragPreview = (clientX, clientY) => {
      dragPreview.style.left = `${clientX}px`;
      dragPreview.style.top  = `${clientY}px`;
    };

    // Position immediately so it doesn't flash at top-left on hold
    if (initialEvent) moveDragPreview(initialEvent.clientX, initialEvent.clientY);

    const commitDrag = () => {
      if (dragCommitted) return;
      dragCommitted = true;
      barItem?.classList.add('dragging');
      document.getElementById('canvas-container')?.classList.add('drop-active');
      dragPreview.style.opacity = '1';
    };

    const cancelDrag = () => {
      cleanup();
      dragPreview.remove();
    };

    const removeDragVisuals = () => {
      barItem?.classList.remove('dragging');
      document.getElementById('canvas-container')?.classList.remove('drop-active');
      dragPreview.style.opacity = '0';
      dragPreview.style.transform = 'translate(-50%, calc(-100% - 10px)) scale(0.85) rotate(2deg)';
      dragPreview.style.transition = 'opacity 0.15s ease, transform 0.15s ease';
      dragPreview.addEventListener('transitionend', () => dragPreview.remove(), { once: true });
    };
    // ─────────────────────────────────────────────────────────────────

    const isOverCanvas = (clientX, clientY) => {
      const el = document.elementFromPoint(clientX, clientY);
      return el === canvas || canvas.contains(el);
    };

    const handleMove = async (clientX, clientY) => {
      const dx = Math.abs(clientX - startX);
      const dy = startY - clientY; // positive = moved upward toward canvas

      // Determine drag vs scroll intent based on first significant movement
      if (!dragCommitted) {
        if (isOverCanvas(clientX, clientY) || dy > 12) {
          commitDrag();
        } else if (dx > 14 && dx > dy + 8) {
          // Primarily horizontal — treat as bar scroll, cancel drag
          cancelDrag();
          return;
        } else {
          // Not enough movement to decide yet; just track position
          moveDragPreview(clientX, clientY);
          return;
        }
      }

      moveDragPreview(clientX, clientY);

      if (!isOverCanvas(clientX, clientY)) return;

      // Create ghost the first time the pointer enters the canvas
      if (!ghostCreated && !ghostCreating) {
        ghostCreating = true;
        await this.selectFurnitureType(furniture);
        ghostCreated = true;
        ghostCreating = false;
      }

      if (ghostCreated) {
        this.app.getEventManager()?.onMouseMove({ clientX, clientY });
      }
    };

    const handleUp = (clientX, clientY) => {
      cleanup();
      removeDragVisuals();

      // No ghost was ever created (pointer never entered canvas) — nothing to do
      if (!ghostCreated) return;

      const furnitureManager = this.app.getFurnitureManager();
      const ghostFurniture = furnitureManager?.getGhostFurniture();
      if (!ghostFurniture?.isVisible) return;

      if (isOverCanvas(clientX, clientY)) {
        this.handleGroundClick(ghostFurniture.getMesh().position);
      } else {
        // Released outside canvas — cancel placement
        this.cleanupAfterPlacement(furnitureManager);
      }
    };

    const onPointerMove = (e) => {
      if (activeSource && activeSource !== 'pointer') return;
      activeSource = 'pointer';
      handleMove(e.clientX, e.clientY);
    };
    const onPointerUp = (e) => {
      if (activeSource && activeSource !== 'pointer') return;
      handleUp(e.clientX, e.clientY);
    };

    const onTouchMove = (e) => {
      activeSource = 'touch';
      const t = e.changedTouches[0];
      // Only block native scroll once committed to a drag
      if (dragCommitted) e.preventDefault();
      handleMove(t.clientX, t.clientY);
    };
    const onTouchEnd = (e) => {
      activeSource = 'touch';
      const t = e.changedTouches[0];
      handleUp(t.clientX, t.clientY);
    };

    const cleanup = () => {
      document.removeEventListener('pointermove', onPointerMove);
      document.removeEventListener('pointerup', onPointerUp);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onTouchEnd);
    };

    document.addEventListener('pointermove', onPointerMove);
    document.addEventListener('pointerup', onPointerUp);
    document.addEventListener('touchmove', onTouchMove, { passive: false });
    document.addEventListener('touchend', onTouchEnd);
  }

  async selectFurnitureType(furniture) {
    // Update UI selection
    clearFurnitureSelection();

    const selectedItem = document.querySelector(
      `[data-furniture-id="${furniture.id}"]`,
    );
    if (selectedItem) {
      selectedItem.classList.add("selected");
    }

    this.selectedFurnitureType = furniture;

    // The ghost previews the placement: on the floor, or on the plus it snaps to.
    const furnitureManager = this.app.getFurnitureManager();
    if (furnitureManager) {
      // Show loading cursor while model loads
      showLoadingCursor();
      await furnitureManager.createGhostFurniture(furniture);
      hideLoadingCursor();
      furnitureManager.showGhostFurniture();
    }

    // Update cursor or provide visual feedback for placement mode
    document.body.classList.add("cursor-crosshair");
    document.body.classList.remove("cursor-default", "cursor-wait");
  }

  async selectMaterial(materialId) {
    const materialManager = this.app.getMaterialManager();
    if (!materialManager) return;

    this.selectedMaterial = materialId;
    this.fabricGrid.updateMaterialSelection();

    // Remember the pick as this browser's preferred colour, for new placements and any preset
    // loaded next (see UserPreferences / applyLastSelectedColorToScene).
    const fabricCode = materialManager.fabrics?.[materialId]?.fabricCode;
    if (fabricCode && fabricCode !== this.lastSelectedColor) {
      this.lastSelectedColor = fabricCode;
      this.app.notifyStateChanged?.();
    }

    // Apply material to ALL selected furniture (supports multi-select)
    const furnitureManager = this.app.getFurnitureManager();
    const selectedFurnitureArray = furnitureManager?.getSelectedFurnitureArray() || [];

    if (selectedFurnitureArray.length > 0) {
      // Apply material to each selected furniture item
      for (const selectedFurniture of selectedFurnitureArray) {
        // Components follow only when they were selected too (double click, Select All).
        const propagate = furnitureManager.isSelectedWithComponents(selectedFurniture);
        await furnitureManager.applyMaterialToFurniture(selectedFurniture, materialId, propagate);
      }
    }
  }

  /**
   * Apply `fabricId` to every item in the scene it is available for.
   * @param {string} fabricId
   * @param {{substituteUnavailable?: boolean}} [opts] when set, an item whose product rejects
   *   `fabricId` gets that product's first available colour instead of being left alone
   * @returns {Promise<{applied: number, skipped: number}>} skipped = no colour could be applied
   */
  async _applyFabricToScene(fabricId, { substituteUnavailable = false } = {}) {
    const furnitureManager = this.app.getFurnitureManager();
    const materialManager = this.app.getMaterialManager();
    if (!furnitureManager || !materialManager) return { applied: 0, skipped: 0 };

    let applied = 0;
    let skipped = 0;
    for (const furniture of furnitureManager.getAllFurniture()) {
      const urlCode = furniture.furnitureData?.urlCode;

      let itemFabricId = fabricId;
      if (!materialManager.isFabricAllowedForProduct(fabricId, urlCode)) {
        itemFabricId = substituteUnavailable ? materialManager.getFirstAvailableFabricId(urlCode) : null;
      }
      if (!itemFabricId) {
        skipped++;
        continue;
      }
      if (furniture.fabricId === itemFabricId) {
        applied++;
        continue;
      }

      // No propagation: getAllFurniture() lists attachments as their own entries, so a module
      // propagating would paint each of them twice — and would override their own availability.
      await furnitureManager.applyMaterialToFurniture(furniture, itemFabricId, false);
      applied++;
    }
    return { applied, skipped };
  }

  /** Fabric code of the last colour picked, persisted by UserPreferences. @returns {string|null} */
  getLastSelectedColor() {
    return this.lastSelectedColor;
  }

  /**
   * Store the preferred fabric code as-is, even when unsold here — resolution happens per use
   * (see _preferredFabricId), so returning to a country that sells it restores the real preference.
   * @param {string} fabricCode
   */
  restoreLastSelectedColor(fabricCode) {
    if (!fabricCode) return;
    this.lastSelectedColor = fabricCode;

    // Not sold here → leave the picker on init()'s default (ET-14, else first in the list).
    const fabricId = this.app.getMaterialManager()?.getFabricIdByCode(fabricCode);
    if (!fabricId) return;

    this.selectedMaterial = fabricId;
    this.fabricGrid.updateMaterialSelection();
  }

  /**
   * The preferred colour as a fabric id, falling back to the first available colour when the
   * stored code isn't sold here; null when none is stored.
   * @returns {string|null}
   */
  _preferredFabricId() {
    if (!this.lastSelectedColor) return null;
    const materialManager = this.app.getMaterialManager();
    return materialManager?.getFabricIdByCode(this.lastSelectedColor)
      ?? materialManager?.getFirstAvailableFabricId()
      ?? null;
  }

  /**
   * Repaint the scene in the user's preferred colour. Presets only — a resumed session or a
   * `?layout=` link carries per-item colours that are somebody's actual choice and must survive.
   */
  async applyLastSelectedColorToScene() {
    const fabricId = this._preferredFabricId();
    if (fabricId) await this._applyFabricToScene(fabricId, { substituteUnavailable: true });
  }

  /**
   * Dev-only: switch model detail and rebuild the scene. Every model is refetched, so this uses
   * the same transition loader as picking a layout.
   * @param {boolean} on
   */
  async setHighPoly(on) {
    const furnitureManager = this.app.getFurnitureManager();
    if (!furnitureManager?.setHighPoly(on)) return;

    // Persist first: the reload is slow and the preference should survive a refresh mid-load.
    this.app.getStateManager()?.saveSceneState();

    this.layoutPicker.showTransitionLoader();
    try {
      await furnitureManager.reloadModels();
      this.updateSelection(null);
      this.updatePerformanceStats();
      this.app.getDimensionManager()?.update();
    } catch (error) {
      console.error('Failed to reload models:', error);
      this.showNotification('Could not reload the models at that detail level.', 'warning');
    } finally {
      this.layoutPicker.hideTransitionLoader();
    }
  }

  openEnvironmentOverlay() {
    const environmentOverlay = document.getElementById("environment-overlay");
    if (environmentOverlay) environmentOverlay.classList.remove("hidden");
  }

  openSettingsModal() {
    const settingsOverlay = document.getElementById("settings-overlay");
    if (settingsOverlay) settingsOverlay.classList.remove("hidden");
  }

  closeOverlay(overlayType) {
    const overlay = document.getElementById(`${overlayType}-overlay`);
    if (overlay) {
      overlay.classList.add("hidden");
    }
  }

  async handleGroundClick(position) {
    if (!this.selectedFurnitureType) {
      return;
    }

    const furnitureManager = this.app.getFurnitureManager();
    if (!furnitureManager) {
      return;
    }

    const ghostFurniture = furnitureManager.getGhostFurniture();

    // Block placement when ghost is out of bounds or colliding with existing furniture.
    // Cancel (clear ghost) rather than silently ignoring the release.
    if (ghostFurniture && !ghostFurniture.isPlaceable) {
      this.cleanupAfterPlacement(furnitureManager);
      return;
    }

    // Check if ghost furniture is snapped to an attachment point
    if (ghostFurniture && ghostFurniture.isSnapped) {
      const snappedAttachmentPoint = ghostFurniture.getSnappedAttachmentPoint();
      if (snappedAttachmentPoint) {
        await this.handleAttachmentSnappedPlacement(furnitureManager, snappedAttachmentPoint);
      }
    } else {
      // Components can only be placed on APs — cancel if not snapped. Accessories are
      // the exception: they stand on the floor on their own.
      const ghostComponentType = ghostFurniture?.furnitureData?.componentType;
      if (ghostComponentType && !isFreePlaceable(ghostComponentType)) {
        this.cleanupAfterPlacement(furnitureManager);
        return;
      }
      // Use the ghost's already-clamped position rather than the raw click position,
      // so furniture always spawns inside the room box walls.
      const placementPosition = ghostFurniture?.getMesh()?.position ?? position;
      await this.handleNormalGroundPlacement(furnitureManager, placementPosition);
    }

    // Clean up after placement
    this.cleanupAfterPlacement(furnitureManager);
  }

  /**
   * Handles furniture placement when snapped to an attachment point
   * @param {FurnitureManager} furnitureManager - The furniture manager instance
   * @param {AttachmentPoint} snappedAttachmentPoint - The attachment point to snap to
   */
  async handleAttachmentSnappedPlacement(furnitureManager, snappedAttachmentPoint) {
    const ghostFurniture = furnitureManager.getGhostFurniture();
    const componentType = this.selectedFurnitureType?.componentType;
    const isComponent = componentType && componentType !== 'module';

    if (isComponent) {
      // Final validity gate — ghost may have reached this AP via the cursor-near-BB fallback
      // before component rules were re-evaluated. Re-check here to be certain.
      const parentFurniture = snappedAttachmentPoint.parentFurniture;
      if (!parentFurniture.shouldShowAttachmentPoint(snappedAttachmentPoint)) return;

      const attachmentOrientation = snappedAttachmentPoint.getAttachmentOrientation();

      // Use ghost's actual position (includes slide offset from EventManager)
      const ghostPos = ghostFurniture.getMesh().position.clone();
      const slideOffset = this.app.getEventManager()?.componentDragSlidOffset;

      showLoadingCursor();
      const furniture = await furnitureManager.spawnFurniture(this.selectedFurnitureType, ghostPos, { preLifted: true });
      hideLoadingCursor();
      if (!furniture) return;

      furniture.getMesh().quaternion.copy(attachmentOrientation);
      furniture.getMesh().position.copy(ghostPos);
      furniture.attachTo(snappedAttachmentPoint);
      // Persist slide offset to AP so it survives save/load
      if (slideOffset) {
        snappedAttachmentPoint.setSlide(furniture, slideOffset);
      }
      furniture.calculateBoundingBox();
      this.finalizeFurniturePlacement(furnitureManager, furniture);
    } else {
      // Module snap placement (existing logic)
      const ghostPosition = ghostFurniture.getMesh().position.clone();
      const ghostRotation = furnitureManager.getGhostFurnitureRotation();
      const childAttachmentSide = ghostFurniture.getChildAttachmentSide();

      showLoadingCursor();
      const furniture = await furnitureManager.spawnFurniture(
        this.selectedFurnitureType,
        ghostPosition,
        { preLifted: true },
      );
      hideLoadingCursor();

      if (!furniture) return;

      furniture.getMesh().rotation.y = ghostRotation;
      furniture.attachTo(snappedAttachmentPoint, childAttachmentSide || null);
      furniture.calculateBoundingBox();
      this.finalizeFurniturePlacement(furnitureManager, furniture);
    }
  }

  /**
   * Handles normal ground placement (not snapped to attachment)
   * @param {FurnitureManager} furnitureManager - The furniture manager instance
   * @param {THREE.Vector3} position - The ground position to place furniture
   */
  async handleNormalGroundPlacement(furnitureManager, position) {
    const ghostRotation = furnitureManager.getGhostFurnitureRotation();

    // Show loading cursor while model loads
    showLoadingCursor();

    const furniture = await furnitureManager.spawnFurniture(this.selectedFurnitureType, position);

    hideLoadingCursor();

    if (!furniture) {
      return;
    }

    // Apply rotation if needed
    if (ghostRotation !== 0) {
      furniture.getMesh().rotation.y = ghostRotation;
      furniture.calculateBoundingBox();
    }

    this.finalizeFurniturePlacement(furnitureManager, furniture);
  }

  /**
   * Finalizes furniture placement by updating stats and applying materials
   * @param {FurnitureManager} furnitureManager - The furniture manager instance
   * @param {FurnitureElement} furniture - The placed furniture
   */
  finalizeFurniturePlacement(furnitureManager, furniture) {
    this.updatePerformanceStats();

    // Apply material if needed (don't auto-select spawned furniture)
    const mm = this.app.getMaterialManager();
    const picked = this.selectedMaterial || mm?.getFabricIdByCode(DEFAULT_FABRIC_CODE);
    // Resolve against this product: the picker's colour comes from whichever grid was open,
    // and a shared accessory wears fewer fabric groups than the modules it sits beside.
    const materialId = mm
      ? mm.resolveFabricForProduct(picked, furniture.furnitureData?.urlCode)
      : picked;
    if (materialId) {
      // Propagate to children for modules, and for combos that auto-attach a
      // child component on spawn (e.g. R+H) so the seated child matches.
      const isModule = !furniture.furnitureData?.componentType ||
                       furniture.furnitureData?.componentType === 'module';
      const propagate = isModule ||
        furniture.furnitureData?.properties?.attachChildOnSpawn?.length > 0;
      furnitureManager.applyMaterialToFurniture(furniture, materialId, propagate);
    }

    // The covers control is driven by what's in scene, so placing the first item that has
    // covers (a TA-1/TA-2 ottoman, a choice module) has to reveal it.
    this.updateCoversUI();

    // Last: the piece has to be in its final place before the group's centre means anything.
    furnitureManager.centerIfAuto();
  }

  /**
   * Cleans up UI state after furniture placement
   * @param {FurnitureManager} furnitureManager - The furniture manager instance
   */
  cleanupAfterPlacement(furnitureManager) {
    furnitureManager.clearGhostFurniture();

    // Reset cursor
    document.body.classList.remove("cursor-crosshair", "cursor-wait", "cursor-default");
    this.selectedFurnitureType = null;
    this.fabricGrid.invalidate();

    // Deselect furniture items in UI
    clearFurnitureSelection();
  }

  /**
   * Sync UI controls to whichever half of the persisted state is passed — the layout from
   * StateManager.loadSceneFromObject(), the preferences from UserPreferences.apply() — keyed
   * off field presence. Keeps DOM manipulation out of StateManager.
   * @param {Object} state - Layout or preferences object
   */
  syncStateToUI(state) {
    // uiStyle is not handled here — setUIStyle() owns the segmented control and the
    // re-parenting that goes with it, and UserPreferences.apply() calls it directly.

    // Environment selection highlight
    const currentEnvKey = state.environment?.environment;
    if (currentEnvKey) this.environmentPanel.markSelectedEnvironment(currentEnvKey);

    // Leg color buttons
    if (state.legTexture) {
      document.querySelectorAll('.leg-color-opt').forEach((btn) => {
        btn.classList.toggle('active', btn.dataset.value === state.legTexture);
      });
    }

    // Leg height inline select
    if (state.legHeight) {
      const legHeightSelectInline = document.getElementById('leg-height-select-inline');
      if (legHeightSelectInline && legHeightSelectInline.value !== state.legHeight) {
        const hasOption = Array.from(legHeightSelectInline.options).some(opt => opt.value === state.legHeight);
        if (hasOption) legHeightSelectInline.value = state.legHeight;
      }
    }

    // Dimensions toggle + unit
    if (state.dimensionsEnabled !== undefined) {
      const dimensionsToggle = document.getElementById('dimensions-toggle');
      if (dimensionsToggle) dimensionsToggle.checked = state.dimensionsEnabled;
    }
    if (state.dimensionUnit) {
      const dimensionUnitSelect = document.getElementById('dimension-unit-select');
      if (dimensionUnitSelect) dimensionUnitSelect.value = state.dimensionUnit;
    }

    // Shadow quality select (migrate legacy PCF tiers onto the VSM option)
    if (state.shadowQuality) {
      const quality = migrateShadowQuality(state.shadowQuality);
      const shadowQualitySelect = document.getElementById('shadow-quality-select');
      if (shadowQualitySelect) shadowQualitySelect.value = quality;
    }

    if (state.camera) {
      if (state.camera.sceneMode) {
        this.updateViewToggleButtons(state.camera.sceneMode);
        this.onSceneModeChanged(state.camera.sceneMode);
      }
    }

    // White floor toggle (already applied to the scene by setEnvironmentState)
    this._syncWhiteFloorToggle();

    // Auto-centre toggle
    if (state.autoCenter !== undefined) {
      const autoCenterToggle = document.getElementById('auto-center-toggle');
      if (autoCenterToggle) autoCenterToggle.checked = !!state.autoCenter;
    }

    // Snapping toggle
    if (state.snappingEnabled !== undefined && state.snappingEnabled !== null) {
      const snappingToggle = document.getElementById('snapping-toggle');
      if (snappingToggle) snappingToggle.checked = state.snappingEnabled;
    }

    // Panning toggle
    if (state.panningEnabled !== undefined && state.panningEnabled !== null) {
      const panningToggle = document.getElementById('panning-toggle');
      if (panningToggle) panningToggle.checked = state.panningEnabled;
    }

    // Disable-collision toggle (dev only)
    if (SHOW_DEV_TOOLS && state.collisionEnabled === false) {
      const disableCollisionToggle = document.getElementById('disable-collision-toggle');
      if (disableCollisionToggle) disableCollisionToggle.checked = true;
    }

    // High-poly toggle (dev only)
    if (SHOW_DEV_TOOLS && state.highPoly !== undefined) {
      const highPolyToggle = document.getElementById('high-poly-toggle');
      if (highPolyToggle) highPolyToggle.checked = !!state.highPoly;
    }

    // Language toggle
    if (state.languageMode) {
      const localBtn = document.getElementById('language-btn-local');
      const enBtn = document.getElementById('language-btn-en');
      if (localBtn && enBtn) {
        const isEn = state.languageMode === 'en';
        localBtn.classList.toggle('active', !isEn);
        enBtn.classList.toggle('active', isEn);
      }
    }

    // Room box controls
    if (state.roomBox) {
      const roomBox = this.app.getSceneManager()?.getRoomBox();
      const toggle = document.getElementById('room-box-toggle');
      if (toggle) toggle.checked = !!state.roomBox.enabled;
      if (roomBox) this._syncRoomBoxControls(roomBox);
      const sizeControls = document.getElementById('room-box-size-controls');
      if (sizeControls) sizeControls.classList.toggle('hidden', !state.roomBox.enabled);
    }
  }

  updateSelection(furnitureOrArray) {
    // Handle both single furniture and array for multi-select
    const furnitureArray = Array.isArray(furnitureOrArray) ? furnitureOrArray : (furnitureOrArray ? [furnitureOrArray] : []);
    const furniture = furnitureArray.length > 0 ? furnitureArray[0] : null;
    const isMultiSelect = furnitureArray.length > 1;

    // Update selected furniture UI elements
    const selectionLabel = document.getElementById("selection-label");
    const selectedName = document.getElementById("selected-furniture-name");
    const rotateBtn = document.getElementById("selected-rotate-btn");

    if (furniture) {
      // Only Layout 1 opens for the fabric grid; Layout 2 leaves the sheet as it found it.
      if (this.uiStyle !== 'layout2' && this.barCollapsed) this.toggleBar();

      // Show top selection label
      if (selectionLabel) selectionLabel.classList.remove("hidden");

      if (isMultiSelect) {
        if (selectedName) selectedName.textContent = this.t('designerSelectionItemsSelected', '{count} items selected', { count: furnitureArray.length });
        this._setSelectionDims('');
      } else {
        this._renderSelectionInfo(furniture);
      }

      // Hidden for anything that can't turn; disabled when the action has no
      // defined behaviour for the current selection.
      if (rotateBtn) {
        const hasRotatable = furnitureArray.some(isFreeStanding);
        rotateBtn.classList.toggle('hidden', !hasRotatable);

        if (hasRotatable && isMultiSelect) {
          const allModules = this.selectAllTargets();
          const isSelectAll = allModules.length > 0 && furnitureArray.length === allModules.length;
          rotateBtn.disabled = !isSelectAll;
          rotateBtn.title = isSelectAll
            ? this.t('designerSelectionRotateGroup', 'Rotate all (R)')
            : this.t('designerSelectionCannotRotateMultiple', 'Cannot rotate multiple items');
        } else if (hasRotatable) {
          // Mirrors the EventManager.rotateModuleAndResnap guard: a mid-chain
          // module (parent + child) or a fork (2+ children) can't be rotated
          // without orphaning a neighbour.
          const moduleChildCount = furniture.attachedChildren.filter(
            c => c.furnitureData?.componentType === 'module'
          ).length;
          const tooManyNeighbors = (furniture.attachedTo ? 1 : 0) + moduleChildCount > 1;
          rotateBtn.disabled = tooManyNeighbors;
          rotateBtn.title = tooManyNeighbors
            ? this.t('designerSelectionCannotRotateAttachments', 'Cannot rotate furniture with attachments')
            : this.t('designerSelectionRotateTooltip', 'Rotate (R)');
        }
      }

      // Show/hide variant (Transform) button based on selected furniture having variants
      const variantBtn = document.getElementById('selected-variant-btn');
      const variantSep = document.getElementById('selected-variant-sep');
      const hasVariants = !isMultiSelect &&
        (furniture?.furnitureData?.properties?.variants?.length > 1);
      variantBtn?.classList.toggle('hidden', !hasVariants);
      variantSep?.classList.toggle('hidden', !hasVariants);
      if (hasVariants) this.updateVariantButton(furniture);

      // Wood finish picker: shown only for selections with a dedicated wood surface.
      this.updateWoodFinishButton(furnitureArray);

      // Switch to material grid only if any selected furniture has fabric
      const hasFabricMaterial = furnitureArray.some(f => {
        const zones = f.furnitureData?.properties?.materialZones;
        return !zones || zones.includes('fabric');
      });
      this._setBarGrid(hasFabricMaterial ? "material" : "furniture");
    } else {
      // Empty-canvas click is a dismiss; deliberately does not move the sheet.

      // Hide variant button when nothing is selected
      document.getElementById('selected-variant-btn')?.classList.add('hidden');
      document.getElementById('selected-variant-sep')?.classList.add('hidden');

      // Hide wood finish button when nothing is selected
      this.updateWoodFinishButton([]);

      // Hide top selection label
      if (selectionLabel) selectionLabel.classList.add("hidden");

      // Switch to furniture grid within unified bar
      this._setBarGrid("furniture");
    }

    // Update material data
    if (furniture) {
      // For single-select, update material selection to match the furniture's current material
      if (!isMultiSelect && furniture.fabricId) {
        this.selectedMaterial = furniture.fabricId;
      } else if (isMultiSelect) {
        // For multi-select, clear material selection (materials may differ)
        this.selectedMaterial = null;
      }

      // Refresh material groups based on selected furniture's allowed fabrics
      this.populateMaterialGrid();
    } else {
      // If selectedMaterial was cleared by a multi-select, restore it to ET-14
      if (!this.selectedMaterial) {
        const materialManager = this.app.getMaterialManager();
        if (materialManager) {
          const defaultFabricId = materialManager.getFabricIdByCode(DEFAULT_FABRIC_CODE);
          if (defaultFabricId) this.selectedMaterial = defaultFabricId;
        }
      }
      this.fabricGrid.invalidate();
    }

    this._updateDrawerInfo(furnitureArray);
    // The header only exists while something is selected; handle and options move with it.
    this._syncDrawerHosts(furnitureArray);
    this.updateSelectAllButton();

    // Header gain/loss changes the sheet height; last, after it is built.
    this._syncCanvasInset();
  }

  /**
   * Union of API-provided leg heights for every `hasLegs` collection currently in the
   * scene (falling back to the active collection filter when the scene is empty). This
   * is the exact set the leg-height dropdown offers, so it's the authority for validating
   * a `?legHeight=` override against what's actually available in this build/country.
   * @returns {string[]} sorted height strings, e.g. ["8", "10.5", "17"]
   */
  getAvailableLegHeightsForScene() {
    const furnitureManager = this.app.getFurnitureManager();
    if (!furnitureManager) return [];

    const collectionsInScene = new Set();
    furnitureManager.getAllFurniture().forEach(furniture => {
      const collection = furniture.furnitureData?.collection;
      if (collection) collectionsInScene.add(collection);
    });
    if (collectionsInScene.size === 0 && this.currentCategory) {
      collectionsInScene.add(this.currentCategory);
    }

    const collections = furnitureManager.getFurnitureCollections();
    const heightsSet = new Set();
    collectionsInScene.forEach(collection => {
      if (collections?.[collection]?.hasLegs !== true) return;
      const heights = furnitureManager.legHeightsData?.collection?.[`${collection}-collection`];
      if (heights) heights.forEach(h => heightsSet.add(h));
    });

    return [...heightsSet].sort((a, b) => parseFloat(a) - parseFloat(b));
  }

  /**
   * Update leg height dropdown based on collections currently in the scene.
   * Called when furniture is added/removed.
   */
  updateLegHeightDropdown() {
    const legHeightSelectInline = document.getElementById('leg-height-select-inline');
    if (!legHeightSelectInline) return;

    const furnitureManager = this.app.getFurnitureManager();
    if (!furnitureManager) return;

    // Heights available for the collections currently in scene (single source of truth,
    // shared with the `?legHeight=` override validation).
    const availableHeights = this.getAvailableLegHeightsForScene();

    if (availableHeights.length === 0) {
      legHeightSelectInline.disabled = true;
      legHeightSelectInline.innerHTML = `<option value="">${this.t('designerLegHeightNone', 'No heights available')}</option>`;
      return;
    }

    // Populate dropdown with available heights
    legHeightSelectInline.innerHTML = '';
    availableHeights.forEach(height => {
      const option = document.createElement('option');
      option.value = height;
      option.textContent = `${height} cm`;
      legHeightSelectInline.appendChild(option);
    });

    // Set current value
    const currentHeight = furnitureManager.currentLegHeight;
    if (availableHeights.includes(currentHeight)) {
      legHeightSelectInline.value = currentHeight;
    } else {
      // Use first available height as default
      legHeightSelectInline.value = availableHeights[0];
      furnitureManager.currentLegHeight = availableHeights[0];
    }

    // Enable dropdown
    legHeightSelectInline.disabled = false;
  }

  showNotification(message, type = "info") {
    // Create notification element
    const notification = document.createElement("div");
    notification.className = `notification notification-${type}`;
    notification.textContent = message;

    document.body.appendChild(notification);

    // Auto remove after 3 seconds
    setTimeout(() => {
      notification.classList.add("notification-slide-out");
      setTimeout(() => {
        if (document.body.contains(notification)) {
          document.body.removeChild(notification);
        }
      }, 300);
    }, 3000);
  }

  handleResize() {
    // Orientation changes which edge the canvas gives up, and how wide the row is.
    this._syncCanvasInset();
    // A wider sheet can hold the row that needed arrows a moment ago.
    this._updateBarScrollButtons();

    // Handle responsive behavior - close overlays on resize
    const isMobile = window.innerWidth <= 768;

    if (isMobile) {
      const settingsOverlay = document.getElementById("settings-overlay");
      const environmentOverlay = document.getElementById("environment-overlay");
      if (settingsOverlay) settingsOverlay.classList.add("hidden");
      if (environmentOverlay) environmentOverlay.classList.add("hidden");
    }
  }

  // Update method called in main render loop
  update(frameTime) {
    this.devTools.trackFrame(frameTime);

    // Update time controls UI
    if (this.timeControlsUI) {
      this.timeControlsUI.update();
    }
  }

  // Scene mode changed callback
  onSceneModeChanged(mode) {
    // Update the view toggle group buttons
    this.updateViewToggleButtons(mode);

    // Re-enable view toggle buttons
    const toggleGroup = document.getElementById("view-toggle-btn");
    if (toggleGroup) {
      toggleGroup.classList.remove("scene-mode-toggle-disabled");
      toggleGroup.classList.add("scene-mode-toggle-enabled");
    }

    // Handle ceiling visibility for 2D mode (make it single-sided so camera can see through from above)
    const roomBuilder = this.app.getSceneManager()?.getRoomBuilder();
    if (roomBuilder) {
      roomBuilder.setCeilingSide(mode === "2D");
    }

    // Disable path tracing in 2D mode
    const ptManager = this.app.getPathTracingManager();
    if (ptManager && mode === "2D" && ptManager.isActive()) {
      ptManager.setEnabled(false);
    }
    this.updatePathTracingButton();
  }

  // Called when transition starts
  onSceneModeTransitionStart() {
    // Disable view toggle during transition
    const toggleGroup = document.getElementById("view-toggle-btn");
    if (toggleGroup) {
      toggleGroup.classList.remove("scene-mode-toggle-enabled");
      toggleGroup.classList.add("scene-mode-toggle-disabled");
    }

    // Clear ghost furniture during mode transitions
    const furnitureManager = this.app.getFurnitureManager();
    if (furnitureManager) {
      furnitureManager.clearGhostFurniture();
    }

    // Reset furniture selection state
    this.selectedFurnitureType = null;
    document.body.classList.remove("cursor-crosshair", "cursor-wait", "cursor-default");
    clearFurnitureSelection();

    // Close any open overlays during transition
    this.closeOverlay("settings");
    this.closeOverlay("environment");
  }

  // ===== AR EXPORT =====
  async handleARExport() {
    showLoadingCursor();
    try {
      const { ok, reason, megabytes } = await presentAR(this.app);
      if (SHOW_DEV_TOOLS && megabytes) console.info(`[ar] model ${megabytes}MB`);
      if (!ok && reason !== "empty") {
        // The two Android failures need different wording: a missing AR module is the
        // user's to fix, a rejected content:// URI is ours.
        const message =
          reason === "arcore-missing"
            ? this.t(
                "designerNotifyArModuleMissing",
                "AR needs Google Play Services for AR, which isn't installed on this device",
              )
            : this.t("designerNotifyArUnavailable", "AR is not available on this device");
        this.showNotification(message, "error");
        console.warn("[ar] unavailable:", reason);
      }
    } catch (error) {
      console.error("AR export error:", error);
      this.showNotification(
        this.t("designerNotifyExportFailed", "Export failed. Please try again."),
        "error"
      );
    } finally {
      hideLoadingCursor();
    }
  }

  // ===== SCREENSHOT =====
  /** Draw the view without editor overlays; returns the canvas, which must be read in this same task. */
  _renderCaptureFrame({ hideSelection = false } = {}) {
    // While a trace is on screen it lives on the tracer's own canvas, not the realtime one.
    const tracedCanvas = this.app.getPathTracingManager()?.presentForCapture() ?? null;
    if (tracedCanvas) return tracedCanvas;
    const renderer = this.app.sceneManager?.renderer;
    if (!renderer) return null;

    const cm = this.app.cameraManager;
    const camera = cm?.sceneMode === "2D" ? cm.orthographicCamera : cm?.perspectiveCamera;
    if (camera) {
      const furnitureManager = this.app.getFurnitureManager();
      const outline = hideSelection ? furnitureManager.selectionOutline?.group : null;
      const outlineVisible = outline?.visible;
      if (outline) outline.visible = false;
      try {
        furnitureManager.withoutAttachmentMarkers(() => this.app.sceneManager.render(camera));
      } finally {
        if (outline) outline.visible = outlineVisible;
      }
    }
    return renderer.domElement;
  }

  async downloadScreenshot() {
    const canvas = this._renderCaptureFrame();
    if (!canvas) return;

    // toBlob rather than toDataURL: a 2x-DPR full-screen canvas as a data URL is a
    // multi-megabyte string held in the JS heap, and the native path needs a blob anyway.
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.92));
    if (!blob) return;

    const result = await saveFile(blob, "build-your-own-sofa-screenshot.jpg", {
      dialogTitle: this.t("designerButtonScreenshot", "Screenshot"),
    });
    if (result === "failed") {
      this.showNotification(
        this.t("designerNotifyExportFailed", "Export failed. Please try again."),
        "error",
      );
    }
  }

  // ===== DESIGN HISTORY =====
  saveDesignToHistory() {
    if (!this.app.getFurnitureManager()?.getAllFurniture().length) {
      this.showNotification(this.t('designerNotifyNothingToSave', 'Add some furniture before saving'), 'info');
      return;
    }
    const saved = this.app.getStateManager()?.saveDesign({
      name: this._designName(),
      thumbnail: this._captureHistoryThumbnail(),
    });
    if (saved) this.showNotification(this.t('designerNotifyDesignSaved', 'Design saved to History'), 'success');
    else this.showNotification(this.t('designerNotifySaveFailed', 'Could not save the design on this device'), 'error');
  }

  // The loaded preset's product name, else the catalog collection's.
  _designName() {
    const presetId = this.app.getStateManager()?.getLoadedPresetId();
    const presetName = presetId && (this.layoutPicker.getProductDescription(presetId)?.name
      || this.layoutPicker.findCombinationById(presetId)?.name);
    if (presetName) return presetName;
    const collection = this.app.getFurnitureManager()?.getFurnitureCollections()?.[this.currentCategory];
    return collection?.name || this.t('designerHistoryUntitled', 'My design');
  }

  // The largest centred 4:3 crop at the History hero's size, halved in steps (one pass from a Retina canvas leaves edges jagged).
  _captureHistoryThumbnail(width = 1200, height = 900, mimeType = canEncodeWebp() ? 'image/webp' : 'image/jpeg') {
    try {
      const source = this._renderCaptureFrame({ hideSelection: true });
      if (!source?.width || !source.height) return null;

      const fit = Math.min(source.width / width, source.height / height);
      let sw = width * fit;
      let sh = height * fit;
      let sx = (source.width - sw) / 2;
      let sy = (source.height - sh) / 2;
      const outW = Math.min(width, Math.round(sw));
      const outH = Math.min(height, Math.round(sh));

      let src = source;
      do {
        const dw = Math.max(outW, Math.round(sw / 2));
        const dh = Math.max(outH, Math.round(sh / 2));
        const next = document.createElement('canvas');
        next.width = dw;
        next.height = dh;
        const ctx = next.getContext('2d');
        ctx.imageSmoothingQuality = 'high';
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, dw, dh);
        ctx.drawImage(src, sx, sy, sw, sh, 0, 0, dw, dh);
        if (src !== source) src.width = 0; // frees the step's backing store now rather than at GC
        src = next;
        sx = sy = 0;
        sw = dw;
        sh = dh;
      } while (sw > outW || sh > outH);

      return src.toDataURL(mimeType, 0.82);
    } catch (error) {
      console.warn('[history] thumbnail capture failed:', error);
      return null;
    }
  }

  // ===== SHARE LAYOUT METHODS =====
  /** Every platform gets the modal for its Email tab; share-sheet platforms reach the sheet from the Link tab. */
  handleShareLayout() {
    const url = this.app.getStateManager()?.generateShareableURL();
    if (!url) {
      this.showNotification(this.t('designerNotifyShareFailed', 'Failed to generate shareable URL'), 'error');
      return;
    }
    this.showShareModal(url);
  }

  async shareLinkViaSheet() {
    const url = document.getElementById('share-url-text')?.textContent;
    if (!url) return;

    const result = await shareOrCopy({
      url,
      title: this.t('designerShareTitle', 'My FEYDOM design'),
    });
    if (result === 'failed') {
      this.showNotification(this.t('designerNotifyCopyFailed', 'Failed to copy URL'), 'error');
    } else if (result === 'copied') {
      this.showNotification(this.t('designerNotifyUrlCopied', 'URL copied to clipboard!'), 'success');
    }
  }

  showShareModal(url) {
    const modal = document.getElementById('share-modal');
    const urlText = document.getElementById('share-url-text');
    if (!modal || !urlText) return;

    urlText.textContent = url;
    const nativeShare = document.getElementById('native-share-btn');
    if (nativeShare) nativeShare.hidden = !hasShareSheet();
    modal.classList.add('modal-visible');
    this.shareModal.open(url);
  }

  async copyUrlToClipboard() {
    const url = document.getElementById('share-url-text')?.textContent;
    if (!url) return;

    if (await copyText(url) === 'failed') {
      this.showNotification(this.t('designerNotifyCopyFailed', 'Failed to copy URL'), 'error');
      return;
    }

    this.showNotification(this.t('designerNotifyUrlCopied', 'URL copied to clipboard!'), 'success');

    const copyBtn = document.getElementById('copy-url-btn');
    if (copyBtn) {
      const originalText = copyBtn.textContent;
      copyBtn.textContent = this.t('designerButtonCopied', 'Copied!');
      copyBtn.classList.add('modal-btn-copied');

      setTimeout(() => {
        copyBtn.textContent = originalText;
        copyBtn.classList.remove('modal-btn-copied');
      }, 2000);
    }
  }

  closeShareModal() {
    document.getElementById('share-modal')?.classList.remove('modal-visible');
  }

  /**
   * Open cart modal
   */
  openCart() {
    const cartManager = this.app.getCartManager();
    if (!cartManager) {
      console.error('CartManager not available');
      this.showNotification(this.t('designerNotifyCartUnavailable', 'Cart is not available'), 'error');
      return;
    }

    // Check if there are any orderable items in the scene
    const furnitureManager = this.app.getFurnitureManager();
    const hasAnyFurniture = furnitureManager && furnitureManager.getAllFurniture().length > 0;
    if (!cartManager.hasItems()) {
      const msg = hasAnyFurniture
        ? this.t('designerNotifyNoCartItems', 'The furniture in your scene is not available for ordering')
        : this.t('designerNotifyAddFurnitureFirst', 'Add furniture to your scene first');
      this.showNotification(msg, 'info');
      return;
    }

    // Open the cart
    cartManager.openCart();
  }

  /**
   * Show the help modal (tutorial clips + shortcuts)
   */
  showShortcutsModal() {
    const modal = document.getElementById('shortcuts-modal');
    if (modal) {
      modal.classList.add('modal-visible');
      this.helpTutorials.open();
    }
  }

  /**
   * Close the shortcuts modal
   */
  closeShortcutsModal() {
    const modal = document.getElementById('shortcuts-modal');
    if (modal) {
      modal.classList.remove('modal-visible');
      this.helpTutorials.close();
    }
  }

  /**
   * Unwind one level of UI, for the Android hardware back button.
   *
   * A ladder, not history.back(): only the layout picker pushes entries (it unwinds via backToCollections / backToEditor).
   *
   * @returns {boolean} true if something was consumed; false means "nothing left to
   *   close", which the caller turns into a confirm-to-exit.
   */
  handleBackNavigation() {
    // Topmost modal first. The layout modal is the first-run entry gate — closing it
    // would leave the user on an empty scene with no way back to it.
    const modals = [...document.querySelectorAll('.modal-visible')];
    if (modals.length) {
      const top = modals[modals.length - 1];
      if (top.id === 'layout-selection-modal') {
        // The modal has its own two-level navigation, so unwind that before the modal
        // itself — otherwise back is inert inside a collection on a first run, where the
        // boot gate below would swallow it.
        const combinations = document.getElementById('combinations-view');
        if (combinations && !combinations.classList.contains('hidden')) {
          this.layoutPicker.backToCollections();
          return true;
        }
        // Boot gate: nothing has been loaded yet, so there is nothing behind it to go to.
        if (this.layoutPicker._layoutGateOpen) return true;
        this.layoutPicker.backToEditor();
        return true;
      }
      if (top.id === 'share-modal') { this.closeShareModal(); return true; }
      if (top.id === 'shortcuts-modal') { this.closeShortcutsModal(); return true; }
      top.classList.remove('modal-visible');
      return true;
    }

    // A section open inside the drawer is its own level of nesting.
    if (this._unwindHamburger()) return true;

    for (const overlay of ['settings', 'environment']) {
      const el = document.getElementById(`${overlay}-overlay`);
      if (el && !el.classList.contains('hidden')) {
        this.closeOverlay(overlay);
        return true;
      }
    }

    // Ghost placement, an in-flight drag, then selection — the same states Escape clears.
    return this.app.getEventManager()?.cancelCurrentInteraction?.() ?? false;
  }

  // ===== SUB-VIEW ENTRY POINTS =====
  // Called from main.js, other managers and tests; the work lives in the sub-views.
  showLayoutSelectionModal() { return this.layoutPicker.showLayoutSelectionModal(); }
  reopenLayoutSelectionModal() { return this.layoutPicker.reopenLayoutSelectionModal(); }
  preloadLayoutThumbnails() { return this.layoutPicker.preloadLayoutThumbnails(); }
  findCombinationById(id) { return this.layoutPicker.findCombinationById(id); }
  loadLayoutById(id, overrides = null) { return this.layoutPicker.loadLayoutById(id, overrides); }
  applyPresetOverrides(overrides) { return this.layoutPicker.applyPresetOverrides(overrides); }

  loadElementByUrlCode(urlCode, overrides = null) { return this.embed.loadElementByUrlCode(urlCode, overrides); }
  applyEmbedPosition(position, opts = {}) { return this.embed.applyEmbedPosition(position, opts); }
  embedPositionState() { return this.embed.embedPositionState(); }
  embedYawFor(itemData, override = null) { return this.embed.embedYawFor(itemData, override); }

  changeEnvironment(environmentKey) { return this.environmentPanel.changeEnvironment(environmentKey); }
  _updateRoomBoxBtnVisibility(environmentKey) { return this.environmentPanel.updateRoomBoxBtnVisibility(environmentKey); }

  updatePathTracingButton() { return this.devTools.updatePathTracingButton(); }
  updatePerformanceStats() { return this.devTools.updatePerformanceStats(); }

  // The `<w> × <d> × <h> cm` line from the product-dimensions API.
  // Empty when the product has no row or the row carries no usable pair.
  _productDimensionsText(productId) {
    const dims = lookupProductRow(this.app.getDimensionManager()?.productDimensions, productId);
    if (!dims) return '';

    const w = dims.productLength;
    const d = dims.productWidth;
    const h = dims.productHeight;
    return (w && d && h) ? `${w} × ${d} × ${h} cm` : (w && d) ? `${d} × ${w} cm` : '';
  }

  // `Bard E3 (Long seater)` over its dimensions; a `?layout=` boot skips the picker, so descriptions may still be loading.
  _renderSelectionInfo(furniture) {
    const selectedName = document.getElementById("selected-furniture-name");
    const productId = activeProductCode(furniture);
    const picker = this.layoutPicker;

    if (!picker.productDescriptions) {
      picker.ensureProductDescriptions().then(() => {
        const selected = this.app.getFurnitureManager()?.getSelectedFurnitureArray() ?? [];
        if (picker.productDescriptions && selected.length === 1) this._renderSelectionInfo(selected[0]);
      });
    }

    const desc = picker.getProductDescription(productId);
    const name = desc?.name || furniture.name || this.t('designerSelectionFurniture', 'Furniture');
    const source = picker.productDescriptions?.[productId];
    const sourceShort = (source?.shortDescription?.en || source?.description?.en || '').trim().toLowerCase();
    const short = UNINFORMATIVE_DESCRIPTIONS.has(sourceShort) ? null : desc?.shortDescription || desc?.description;
    // Some API names already carry the description ("Oiko Three-seater" / "Three-seater").
    const repeats = !short || name.toLowerCase().includes(short.toLowerCase());
    if (selectedName) selectedName.textContent = repeats ? name : `${name} (${short})`;
    this._setSelectionDims(this._productDimensionsText(productId));
  }

  _setSelectionDims(text) {
    const dims = document.getElementById("selected-furniture-dims");
    if (!dims) return;
    dims.textContent = text;
    dims.classList.toggle('hidden', !text);
  }

  async setFurnitureCollectionFilter(collectionId, showNotification = true) {
    // Set the current category to the selected collection
    if (collectionId !== this.currentCategory) this.furnitureGrid.collapseAllGroups();
    this.currentCategory = collectionId;

    // Update the furniture grid to show only this collection
    await this.furnitureGrid.updateFurnitureGrid();

    const furnitureManager = this.app.getFurnitureManager();
    const collections = furnitureManager?.getFurnitureCollections();
    const collection = collections?.[collectionId];
    const collectionName = collection?.name || collectionId;

    this._syncLegOptions(collectionId);

    // Covers button visibility + rows are driven by what's in scene
    this.updateCoversUI();

    // Show notification to user
    if (showNotification) {
      this.showNotification(this.t('designerNotifyCollectionFilter', 'Furniture catalog: {collection} collection', { collection: collectionName }), 'info');
    }
  }

  /**
   * Update the Transform button label to show the current variant name.
   * @param {FurnitureElement} furnitureElement
   */
  updateVariantButton(furnitureElement) {
    const variantBtn = document.getElementById('selected-variant-btn');
    if (!variantBtn || !furnitureElement) return;
    const variant = furnitureElement.furnitureData?.properties?.variants?.[furnitureElement.variantIndex ?? 0];
    const variantText = variantBtn.querySelector('.selected-action-text');
    if (variantText && variant?.name) variantText.textContent = variant.name;
  }
}
